import React from 'react';
import { isOtnEndpointAlarm } from '../../common/functions/otn_new_implementation.function';
import { convertDateToLocalString } from '../../common/functions/convert_date_to_local_string.function';

const formatDuration = (startTime, endTime = null) => {
  if (!startTime) {
    return null;
  }

  try {
    const start = new Date(startTime);
    if (isNaN(start.getTime())) {
      return null;
    }

    const end = endTime ? new Date(endTime) : new Date();
    if (isNaN(end.getTime())) {
      return null;
    }

    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) {
      return null;
    }

    const totalMinutes = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    const parts = [];

    if (days > 0) {
      parts.push(`${days}d`);
    }
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    if (minutes > 0) {
      parts.push(`${minutes}m`);
    }

    return parts.length ? parts.join(' ') : '< 1m';
  } catch {
    return null;
  }
};

const formatCommissionDate = (value) => {
  if (!value) {
    return '--';
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return '--';
  }
  return date.toLocaleDateString('en-GB');
};

const formatLength = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }
  return `${parseFloat(value).toFixed(2)} km`;
};

const isHistoryAlarmType = (alarmType) => alarmType === 'history' || alarmType === 'History';

export const getOtnAlarmSummary = (alarm) => {
  const activeCount = [alarm?.end_a_status, alarm?.end_b_status].filter(
    (status) => status === 'Red'
  ).length;

  return {
    activeCount,
    hasActiveAlarm: activeCount > 0,
    isAllClear: activeCount === 0,
  };
};

const StatusDot = ({ active }) => (
  <span className={`otn-dot ${active ? 'otn-dot--active' : 'otn-dot--clear'}`} />
);

const DetailRow = ({ label, value }) => (
  <div className="otn-detail-row">
    <span className="otn-detail-row__label">{label}</span>
    <span className="otn-detail-row__value">{value || '--'}</span>
  </div>
);

const EndpointMetaRows = ({ rows }) => {
  if (!rows?.length) {
    return null;
  }

  return (
    <div className="otn-end__meta-wrap">
      {rows.map(({ label, value }) => (
        <p className="otn-end__meta" key={label}>
          <span className="otn-end__meta-label">{label}</span>
          <span className="otn-end__meta-value">{value}</span>
        </p>
      ))}
    </div>
  );
};

const buildEndpointMetaRows = ({ status, activeTs, inactiveTs, alarmType }) => {
  const isDown = status === 'Red';
  if (!isDown) {
    return [];
  }

  const isHistory = isHistoryAlarmType(alarmType);
  const rows = [];

  if (activeTs) {
    rows.push({
      label: 'Down since:',
      value: convertDateToLocalString(activeTs),
    });
  }

  if (isHistory && inactiveTs) {
    rows.push({
      label: 'Cleared at:',
      value: convertDateToLocalString(inactiveTs),
    });
  }

  const duration = isHistory ? formatDuration(activeTs, inactiveTs) : formatDuration(activeTs);

  if (duration) {
    rows.push({
      label: 'Down for:',
      value: duration,
    });
  }

  return rows;
};

const EndpointBlock = ({ alarmId, status, activeTs, inactiveTs, alarmType }) => {
  const isDown = status === 'Red';
  const metaRows = buildEndpointMetaRows({ status, activeTs, inactiveTs, alarmType });

  return (
    <div className={`otn-end ${isDown ? 'otn-end--active' : 'otn-end--clear'}`}>
      <div className="otn-end__top">
        <p className="otn-end__id">{alarmId || '--'}</p>
        <StatusDot active={isDown} />
      </div>
      <EndpointMetaRows rows={metaRows} />
    </div>
  );
};

export const OtnLinkAlarmCard = ({ alarm, alarmType }) => {
  const { hasActiveAlarm } = getOtnAlarmSummary(alarm);
  const isHistory = isHistoryAlarmType(alarmType);
  const badgeLabel = hasActiveAlarm ? (isHistory ? 'Cleared' : 'Alarm') : 'Clear';
  const badgeClass = hasActiveAlarm ? (isHistory ? 'is-cleared' : 'is-alarm') : 'is-clear';

  return (
    <article className={`otn-card ${hasActiveAlarm ? 'otn-card--alarm' : 'otn-card--clear'}`}>
      <div className="otn-card__head">
        <h3 className="otn-card__title">{alarm?.layer_name}</h3>
        <span className={`otn-card__badge ${badgeClass}`}>{badgeLabel}</span>
      </div>

      <div className="otn-card__details">
        <DetailRow label="Commission Date" value={formatCommissionDate(alarm?.commissioned_at)} />
        <DetailRow label="Length" value={formatLength(alarm?.length)} />
      </div>

      <div className="otn-card__ends">
        <EndpointBlock
          alarmId={alarm?.end_a_alarm_id}
          status={alarm?.end_a_status}
          activeTs={alarm?.end_a_alarm_active_ts}
          inactiveTs={alarm?.end_a_alarm_inactive_ts}
          alarmType={alarmType}
        />
        <EndpointBlock
          alarmId={alarm?.end_b_alarm_id}
          status={alarm?.end_b_status}
          activeTs={alarm?.end_b_alarm_active_ts}
          inactiveTs={alarm?.end_b_alarm_inactive_ts}
          alarmType={alarmType}
        />
      </div>
    </article>
  );
};

export const OtnEndpointAlarmDetails = OtnLinkAlarmCard;

export const OtnEndpointStatusBadge = ({ status }) => <StatusDot active={status === 'Red'} />;

export const OtnEndpointsTableCell = ({ alarm, alarmType = 'live' }) => {
  if (!isOtnEndpointAlarm(alarm)) {
    return null;
  }

  const renderEndpoint = (id, status, activeTs, inactiveTs) => {
    const isDown = status === 'Red';
    const metaRows = buildEndpointMetaRows({ status, activeTs, inactiveTs, alarmType });

    return (
      <div className={`otn-table-end ${isDown ? 'otn-table-end--active' : ''}`}>
        <div className="otn-table-end__header">
          <span className="otn-table-end__id">{id || '--'}</span>
          <StatusDot active={isDown} />
        </div>
        {metaRows.length > 0 && (
          <div className="otn-table-end__meta-wrap">
            {metaRows.map(({ label, value }) => (
              <span className="otn-table-end__meta" key={label}>
                {label} {value}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="otn-table-ends">
      {renderEndpoint(
        alarm?.end_a_alarm_id,
        alarm?.end_a_status,
        alarm?.end_a_alarm_active_ts,
        alarm?.end_a_alarm_inactive_ts
      )}
      {renderEndpoint(
        alarm?.end_b_alarm_id,
        alarm?.end_b_status,
        alarm?.end_b_alarm_active_ts,
        alarm?.end_b_alarm_inactive_ts
      )}
    </div>
  );
};

export const OtnEndpointsExpandedDetails = ({ alarm, alarmType }) => {
  if (!isOtnEndpointAlarm(alarm)) {
    return null;
  }

  return (
    <div className="otn-expanded">
      <OtnLinkAlarmCard alarm={alarm} alarmType={alarmType} />
    </div>
  );
};
